import React from 'react'
import styled from '@emotion/styled'
import { useSelector } from 'react-redux'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { addToast } from '@mobi/component-library/Feedback/Toast'
import { colors } from '@mobi/component-library/Foundations'
import { isReactNativeAndroid as isAndroid, isReactNativeApp } from '@mobi/web-native-comms/web'
import {
  ShowNotificationAuthPopup,
  checkNotificationStatusAsync,
} from '@core/Components/PushNotification/NotificationAuthPopup'
import { store } from '@core/Store'
import { useClickOutside } from '@core/Utils/hooks/useClickOutside'
import { registerNotification, unregisterNotification } from '../Store'
import { getRegisteredNotifications } from '../Store/selectors'
import type { NotificationTime } from '../types'
import { type RaceAlertAnalyticsData, TimePickerPopover } from './TimePickerPopover'
import { useValidNotificationOptions } from './hooks/useValidNotificationTimes'

export const NotificationButton: React.FC<{
  /** the title shown in the push notification */
  eventName: string
  ast: string
  eventType: 'Race' | 'Sport'
  eventId: string
  deepLink?: string
  iconSize?: 'sm' | 'md'
  isHeaderPinned?: boolean
  buttonComponent?: React.FC<React.HTMLAttributes<HTMLButtonElement>>
}> = ({
  eventId,
  ast,
  isHeaderPinned,
  deepLink,
  eventName,
  eventType,
  buttonComponent,
  iconSize = 'sm',
}) => {
  if (!isReactNativeApp && process.env.NODE_ENV !== 'development') {
    return null
  }

  return (
    <NotificationButtonComponent
      eventId={eventId}
      eventName={eventName}
      eventType={eventType}
      deepLink={deepLink || null}
      ast={ast}
      iconSize={iconSize}
      isHeaderPinned={isHeaderPinned}
      buttonComponent={buttonComponent}
    />
  )
}

// ===============
// Local Component
// ===============

const NotificationButtonComponent: React.FC<
  NotificationDetails & {
    iconSize: 'sm' | 'md'
    isHeaderPinned?: boolean
    buttonComponent?: React.FC<React.HTMLAttributes<HTMLButtonElement>>
  }
> = ({
  eventId,
  ast,
  isHeaderPinned,
  deepLink,
  eventName,
  eventType,
  buttonComponent,
  iconSize,
}) => {
  const registeredNotifications = useSelector(getRegisteredNotifications)
  const selectedNotificationTimesRef = React.useRef<NotificationTime[]>([])
  const contextMenuRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const times = registeredNotifications[eventId] || []
    setSelectedTimes(new Set(times))
    selectedNotificationTimesRef.current = times
  }, [eventId, registeredNotifications])

  const [selectedTimes, setSelectedTimes] = React.useState<Set<NotificationTime>>(
    () => new Set(selectedNotificationTimesRef.current)
  )
  const [shouldShake, setShouldShake] = React.useState(false)
  const [isTimeSelectMenuOpen, setIsTimeSelectMenuOpen] = React.useState<boolean>(false)

  useClickOutside(contextMenuRef, () => setIsTimeSelectMenuOpen(false))

  const analyticsDataRef = React.useRef<RaceAlertAnalyticsData>(undefined)
  analyticsDataRef.current = { selectedTimes, ast, isHeaderPinned, eventId }

  const validNotificationOptions = useValidNotificationOptions(ast)

  if (!validNotificationOptions.length) {
    return null
  }

  const prevSelectedTimes = new Set(selectedTimes)

  const isNotificationOn =
    Array.from(selectedTimes.values()).some(x => validNotificationOptions.includes(x)) ||
    selectedNotificationTimesRef.current.some(x => validNotificationOptions.includes(x))

  const onNotificationToggle = async (
    action: 'register' | 'deregister',
    _selectedTimes?: Set<NotificationTime>
  ) => {
    const fcmToken = await getFcmToken()
    if (!fcmToken) return

    let respSuccess = false
    if (action === 'register') {
      const minutesBeforeStart = Array.from(_selectedTimes ?? []).sort((a, b) => a - b)
      const details = { eventId, deepLink, eventType, eventName, ast }
      respSuccess = await register({ details, fcmToken, minutesBeforeStart, isAndroid })
    } else if (action === 'deregister') {
      respSuccess = await unregister({ eventId, fcmToken })
    }

    if (!respSuccess) {
      setSelectedTimes(prevSelectedTimes)
      const errorTitle = 'There was a problem. Please try later.'
      addToast({ id: 'alert-error', title: errorTitle, icon: 'error' })
    }
  }

  const onRegister = (notificationTimes: Set<NotificationTime>) => {
    setShouldShake(true)
    setTimeout(() => setShouldShake(false), 820)
    onNotificationToggle('register', notificationTimes)
  }

  const onDeregister = () => onNotificationToggle('deregister')

  const handleNotificationButtonClick: React.MouseEventHandler = () => {
    setIsTimeSelectMenuOpen(prev => !prev)
  }

  const NotificationButtonComponent = buttonComponent || NotificationButtonStyled

  return (
    <div ref={contextMenuRef}>
      <NotificationButtonComponent
        onClick={handleNotificationButtonClick}
        data-testid='notification-button'
        style={{ background: isTimeSelectMenuOpen ? colors.neutral[100] : 'none' }}
      >
        <ShakeWrapperStyled shake={shouldShake}>
          <Icon
            size={iconSize === 'sm' ? '1.6rem' : '2rem'}
            name={isNotificationOn ? 'SolidBellRinging01' : 'LineBellPlus'}
            color={isNotificationOn ? colors.studio[500] : colors.neutral[900]}
          />
        </ShakeWrapperStyled>
      </NotificationButtonComponent>
      {isTimeSelectMenuOpen && (
        <TimePickerPopover
          data-testid='notification-popover'
          registerAlert={onRegister}
          deregisterAlert={onDeregister}
          selectedTimes={selectedTimes}
          setSelectedTimes={setSelectedTimes}
          validNotificationOptions={validNotificationOptions}
          contextMenuRef={contextMenuRef}
          analyticsDataRef={analyticsDataRef as React.RefObject<RaceAlertAnalyticsData | null>}
          isTimeSelectMenuOpen={isTimeSelectMenuOpen}
          setIsTimeSelectMenuOpen={setIsTimeSelectMenuOpen}
        />
      )}
    </div>
  )
}

// =============
// Local Helpers
// =============

async function getFcmToken() {
  const notificationStatus = await checkNotificationStatusAsync()

  if (notificationStatus.status !== 'Authorized') {
    const reason =
      'Notifications must be enabled on TABtouch to set a race alert. Enable notifications then try again.'
    ShowNotificationAuthPopup({ reason, notificationAuthStatus: notificationStatus.status })
    return
  }

  if (!notificationStatus.fcmRegistrationToken) {
    const errorTitle = `Unable to set alert. ${JSON.stringify(notificationStatus)}`
    addToast({ id: 'alert-error', title: errorTitle, icon: 'error' })
    return
  }

  return notificationStatus.fcmRegistrationToken
}

function register({
  details,
  fcmToken,
  minutesBeforeStart,
  isAndroid,
}: {
  details: NotificationDetails
  fcmToken: string
  minutesBeforeStart: NotificationTime[]
  isAndroid: boolean
}) {
  const payload: EventRegistrationV2Body = {
    eventId: details.eventId,
    eventName: details.eventName,
    eventType: details.eventType,
    ast: details.ast,
    fcmToken,
    minutesBeforeStart: minutesBeforeStart.join(','),
    deepLink: details.deepLink,
    isAndroid,
  }

  // use fancy new v2
  return fetch('/api/notification/v2/register', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
    .then(() => {
      store.dispatch(
        registerNotification({ eventId: details.eventId, notificationTimes: minutesBeforeStart })
      )
      addToast({
        id: 'alert-success',
        isDismissable: true,
        title: 'Alert set.',
        icon: 'success',
      })
      return true
    })
    .catch(() => false)
}

function unregister(eventDeregistration: { eventId: string; fcmToken: string }) {
  return fetch('/api/notification/deregister', {
    method: 'POST',
    body: JSON.stringify(eventDeregistration),
  })
    .then(() => {
      store.dispatch(unregisterNotification(eventDeregistration.eventId))
      addToast({
        id: 'alert-cancelled',
        isDismissable: true,
        title: 'Alert has been cancelled',
        icon: 'success',
      })
      return true
    })
    .catch(() => false)
}

// ======
// Styles
// ======

const NotificationButtonStyled = styled.button({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '3rem',
  width: '3rem',
  padding: 0,
  background: 'none',
  border: 'none',
  borderRadius: '100%',
  cursor: 'pointer',
})

const ShakeWrapperStyled = styled.div<{ shake: boolean }>(
  {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',

    '@keyframes shakeAnimation': {
      '10%, 90%': {
        transform: 'translate3d(-0.1rem, 0, 0)',
      },
      '20%, 80%': {
        transform: 'translate3d(0.1rem, 0, 0)',
      },
      '30%, 50%, 70%': {
        transform: 'translate3d(-0.1rem, 0, 0)',
      },
      '40%, 60%': {
        transform: 'translate3d(0.1rem, 0, 0)',
      },
    },
  },
  ({ shake }) =>
    shake && {
      animation: 'shakeAnimation 0.82s cubic-bezier(0.36, 0.07, 0.19, 0.97) both',
    }
)

// =====
// Types
// =====

export type NotificationEventType = 'Race' | 'Sport'

interface NotificationDetails {
  eventId: string
  deepLink: string | null
  eventType: NotificationEventType
  eventName: string
  ast: string
}

interface EventRegistrationV2Body {
  /** This is the id of the race/sport game etc */
  eventId: string
  /** event start time */
  ast: string
  fcmToken: string
  minutesBeforeStart: string
  /** Name to show in notification text */
  eventName: string
  /** Optional Notification url link */
  deepLink: string | null
  isAndroid: boolean
  eventType: NotificationEventType
}
